import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  colors,
  Input,
} from '@nextui-org/react';
import { FormEvent, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { confirmPasswordResetThunk } from '../store/user/userThunks';
import { generateGradients, MOTION_KEYS } from '../util';
import { GENERIC_APP } from '../config/generic';
import { SOS_APP } from '../config/sos';

export default function ResetPasswordPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialUsername = searchParams.get('username') ?? '';
  const initialCode = searchParams.get('code') ?? '';

  const [username, setUsername] = useState(initialUsername);
  const [code, setCode] = useState(initialCode);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const name =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? GENERIC_APP.name
      : SOS_APP.name;

  const passwordsMatch = useMemo(
    () => password.length > 0 && password === confirmPassword,
    [confirmPassword, password]
  );

  const canSubmit = username && code && password && passwordsMatch;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }

    setSubmitting(true);
    const success = await dispatch(
      confirmPasswordResetThunk({ username, code, password })
    );
    setSubmitting(false);

    if (success) {
      navigate('/login');
    }
  };

  return (
    <motion.div
      variants={{
        [MOTION_KEYS.INITIAL]: {},
        [MOTION_KEYS.ANIMATE]: {
          background: generateGradients([
            colors.red[200],
            colors.yellow[200],
            colors.green[200],
            colors.cyan[200],
            colors.blue[200],
            colors.purple[200],
          ]),
          transition: { duration: 60, repeat: Infinity, ease: 'linear' },
        },
        [MOTION_KEYS.EXIT]: {},
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.EXIT}
      className="flex flex-col justify-center items-center w-screen min-h-screen px-4"
    >
      <motion.div className="w-full max-w-lg">
        <Card className="w-full py-8">
          <CardHeader className="flex flex-col justify-center py-12">
            <p className="text-4xl font-semibold text-center">{name}</p>
            <p className="text-sm text-default-500 mt-3 text-center">
              Enter the reset code from your email and choose a new password.
            </p>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardBody className="flex flex-col mx-auto gap-4 max-w-md w-full">
              <Input
                autoFocus={!initialUsername}
                isRequired
                type="email"
                label="Email"
                placeholder="Email address"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <Input
                autoFocus={Boolean(initialUsername) && !initialCode}
                isRequired
                label="Reset code"
                placeholder="Enter the code from your email"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <Input
                isRequired
                type="password"
                label="New password"
                placeholder="Choose a new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Input
                isRequired
                type="password"
                label="Confirm password"
                placeholder="Re-enter your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                isInvalid={confirmPassword.length > 0 && !passwordsMatch}
                errorMessage={
                  confirmPassword.length > 0 && !passwordsMatch
                    ? 'Passwords must match'
                    : undefined
                }
              />
            </CardBody>

            <CardFooter className="flex flex-col items-center gap-4">
              <Button
                type="submit"
                color="primary"
                isLoading={submitting}
                isDisabled={!canSubmit}
              >
                Set new password
              </Button>
              <Button variant="light" onPress={() => navigate('/login')}>
                Back to login
              </Button>
            </CardFooter>
          </form>
        </Card>
      </motion.div>
    </motion.div>
  );
}
